import { Feature } from "ol";
import { Geometry, LineString } from "ol/geom";
import { VoyageRoute, VoyageRouteLineFeature, VoyageRoutePointFeature } from "../../models/voyage-routing-model";
import { CreateDateLineAndGreatCircleFeatures, getLegTypeFromDist } from "../map-utils";
import { point, polygon, lineString as turfLine, feature, Point } from '@turf/helpers';
import { CoordinatesUtils } from "../coordinates-utils";
import { RouteGeometry } from '../../models/voyage-routing-model';
import { CrossedGeometries } from "../geometries-intersection";
import { getWaypointsFromRoute } from "../helpers";
import { ZoneFeature } from "../../models/zones-base-interfaces";
import midpoint from '@turf/midpoint';
import lineIntersect from '@turf/line-intersect';
import circle from "@turf/circle";
import booleanWithin from "@turf/boolean-within";
import booleanOverlap from "@turf/boolean-overlap";
import { IndexedDBService } from '../../../indexed-db/services/db_service';
import { isOnline } from '../helpers';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

const INTERSECTION_TOLERANCE = 0.463 / 2; // diameter of the circle 0.463 Km = 0.25 Nm 

export interface ZonesUtilities {
    applyToRoute(route: VoyageRoute): Promise<VoyageRoute>;
    routeCrossingZones(route: RouteGeometry): Promise<boolean>;
}

export abstract class ZonesUtilitiesBase implements ZonesUtilities {
    abstract zoneID: string;
    abstract zoneStoreType: string;
    abstract getZones(route?: RouteGeometry): Promise<ZoneFeature[]>;
    
    setAppliedFlagOnRoute(route: VoyageRoute): void {};
    
    // modify the properties of the waypoint inside the zone
    modifyWaypointProperties(waypoint: VoyageRoutePointFeature, zones: ZoneFeature[]): void {};
    
    async routeCrossingZones(route: RouteGeometry) {
        const zones = await this.getZones(route);
        if (!zones) {
            throw Error("Expected valid zones");
        }

        return CrossedGeometries<ZoneFeature>(route, zones!).length > 0;
    }

    async applyToRoute(route: VoyageRoute): Promise<VoyageRoute> {
        const waypoints = getWaypointsFromRoute(route.route_geometry);

        let zones: ZoneFeature[] | null = null;

        if (isVesselOnCloud && !isOnline()) {
            // When offline or slow network on cloud, fetch zones directly from IndexedDB
            console.log(`[ZonesUtilities] Offline - fetching ${this.zoneStoreType} zones from IndexedDB`);
            zones = await IndexedDBService.zones.getByType(this.zoneStoreType);
            console.log(`[ZonesUtilities] IndexedDB returned:`, zones ? `${zones.length} zones` : 'null');
        }

        if (!zones) {
            zones = await this.getZones(waypoints);
        }

        if (!zones) {
            throw Error("Expected valid zones");
        }

        const newWaypoints = JSON.parse(JSON.stringify(this._insertWaypointsAtZonesCrossing(waypoints, zones)));

        this._modifyWaypointsOnCrossingZones(newWaypoints, zones);
        this.restoreOriginalWaypoints(newWaypoints, waypoints);

        const routeGeometry = this._generateRouteGeometry(newWaypoints, route);
        route.route_geometry = routeGeometry;
        this.setAppliedFlagOnRoute(route);

        return route;
    }

    restoreOriginalWaypoints(newWaypoints: VoyageRoutePointFeature[], oldWaypoints: VoyageRoutePointFeature[]) {
        const usedIndices = new Set<number>();

        for (let i=0; i<newWaypoints.length; i++) {
            const wp = newWaypoints[i];
            const matchIndex = oldWaypoints.findIndex((candidate, index) =>
                !usedIndices.has(index) && this._waypointsMatch(candidate, wp)
            );

            if (matchIndex >= 0) {
                usedIndices.add(matchIndex);
                newWaypoints[i] = oldWaypoints[matchIndex];
            }
        }
    }

    private _waypointsEqual(w1: VoyageRoutePointFeature, w2: VoyageRoutePointFeature) {
        return w1.geometry.coordinates[0].toFixed(5) === w2.geometry.coordinates[0].toFixed(5) 
            && w1.geometry.coordinates[1].toFixed(5) === w2.geometry.coordinates[1].toFixed(5)
    }

    private _waypointsMatch(w1: VoyageRoutePointFeature, w2: VoyageRoutePointFeature) {
        if (!this._waypointsEqual(w1, w2)) {
            return false;
        }

        const stoppageEnd1 = w1.properties?.stoppage_end === true;
        const stoppageEnd2 = w2.properties?.stoppage_end === true;
        if (stoppageEnd1 !== stoppageEnd2) {
            return false;
        }

        const stoppage1 = w1.properties?.is_stoppage_point === true;
        const stoppage2 = w2.properties?.is_stoppage_point === true;
        if (stoppage1 !== stoppage2) {
            return false;
        }

        const pilot1 = w1.properties?.pilotPoint === true;
        const pilot2 = w2.properties?.pilotPoint === true;
        if (pilot1 !== pilot2) {
            return false;
        }

        return true;
    }

    private _waypointsOverlap(p1: VoyageRoutePointFeature, p2: VoyageRoutePointFeature) {
        return this._waypointsEqual(p1, p2) || booleanOverlap(circle(p1.geometry.coordinates, INTERSECTION_TOLERANCE, {steps: 300, units: 'kilometers'}), circle(p2.geometry.coordinates, INTERSECTION_TOLERANCE, {steps: 300, units: 'kilometers'})) ||
               booleanWithin(circle(p1.geometry.coordinates, INTERSECTION_TOLERANCE, {steps: 300, units: 'kilometers'}), circle(p2.geometry.coordinates, INTERSECTION_TOLERANCE, {steps: 300, units: 'kilometers'}));
    }

    private _modifyWaypointsOnCrossingZones(waypoints: VoyageRoutePointFeature[], zones: ZoneFeature[]) {
        for (let k = 0; k < waypoints.length - 2; k++) {
            const p1 = point(waypoints[k].geometry.coordinates);
            const p2 = point(waypoints[k+1].geometry.coordinates);
            const p3 = point(waypoints[k+2].geometry.coordinates);
            const p12 = midpoint(p1, p2);
            const p23 = midpoint(p2, p3);

            const p12z: ZoneFeature[] = [];
            const p23z: ZoneFeature[] = [];

            zones!.forEach(z => {
                const polygons = [];
                if (z!.geofence!.type === "MultiPolygon") {
                    (<number[][][][]>z!.geofence!.coordinates).forEach(p => polygons.push(<number[][][]>p));
                }
                if (z!.geofence!.type === "Polygon") {
                    polygons.push(<number[][][]>z!.geofence!.coordinates);
                }

                polygons.forEach(p => {
                    const zoneGeometry = polygon(<number[][][]>p);

                    if (booleanOverlap(circle(p12.geometry.coordinates, INTERSECTION_TOLERANCE, {steps: 300, units: 'kilometers'}), zoneGeometry)
                        || booleanWithin(circle(p12.geometry.coordinates, INTERSECTION_TOLERANCE, {steps: 300, units: 'kilometers'}), zoneGeometry)) {
                            p12z.push(z); 
                    }

                    if (booleanOverlap(circle(p23.geometry.coordinates, INTERSECTION_TOLERANCE, {steps: 300, units: 'kilometers'}), zoneGeometry)
                        || booleanWithin(circle(p23.geometry.coordinates, INTERSECTION_TOLERANCE, {steps: 300, units: 'kilometers'}), zoneGeometry)) {
                            p23z.push(z); 
                    }
                })
            });

            if (p12z.length === 0 && p23z.length > 0) {
                if (p23z.length === 1) {
                    waypoints[k+1].properties.name = `ENTER - ${p23z[0].location_name}`;
                } else {
                    waypoints[k+1].properties.name = `ENTER - ${this.zoneID}`;
                }

                this.modifyWaypointProperties(waypoints[k+1], p23z);
            }

            if (p12z.length > 0 && p23z.length === 0) {
                if (p12z.length === 1) {
                    waypoints[k+1].properties.name = `EXIT - ${p12z[0].location_name}`;
                } else {
                    waypoints[k+1].properties.name = `EXIT - ${this.zoneID}`;
                }
                
                this.modifyWaypointProperties(waypoints[k], p12z);
            }

            if (p12z.length > 0 && p23z.length > 0) {
                if (p12z.length === 1 && p23z.length === 1 && p12z[0].location_name !== p23z[0].location_name) {
                    waypoints[k+1].properties.name = `ENTER ${p23z[0].location_name}`;
                }
                this.modifyWaypointProperties(waypoints[k], p12z);
                this.modifyWaypointProperties(waypoints[k+1], p23z);
            }
        }
    }

    private _getOrigLineString(from: [number,number], to: [number,number], origRoute: VoyageRoute): VoyageRouteLineFeature|null
    {
        const origLine = <VoyageRouteLineFeature|undefined>origRoute!.route_geometry.find( f => {
            if (f.geometry.type === "LineString")
            {
                const margin = 0.0001;
                const coords = f.geometry.coordinates;
                return CoordinatesUtils.isCoordinatesEqual(coords[0], from, margin) &&
                       CoordinatesUtils.isCoordinatesEqual(coords[1], to, margin);
            }
            return false;
        });

        return origLine ? origLine : null;
    }

    private _generateRouteGeometry(waypoints: VoyageRoutePointFeature[], origRoute: VoyageRoute) {
        if (!waypoints || waypoints.length < 2) {
            throw "Invalid waypoints";
        }

        const route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature> = [];

        for (let i=0; i < waypoints.length; i++) {
            route_geometry.push(waypoints[i]);

            if (i < waypoints.length-1) {
                // Add LineString
                const from = waypoints[i].geometry.coordinates;
                const to = waypoints[i+1].geometry.coordinates;
                const line = <VoyageRouteLineFeature>{
                    type: "Feature",
                    geometry: {
                        coordinates: [from, to],
                        type: "LineString",
                    },
                    properties: {
                        leg_type: waypoints[i].properties.leg_type ? waypoints[i].properties.leg_type : getLegTypeFromDist(from, to)
                    }
                };

                if (waypoints[i].properties.isPassed || waypoints[i].properties.is_stoppage_point)
                {
                    // Try to find the original LineString and preserve the properties from there (keeping the new leg_type since it
                    // could have been changed)
                    const origLine = this._getOrigLineString(from, to, origRoute);
                    if (origLine)
                    {
                        line.properties = origLine.properties;
                        line.properties.leg_type = line.properties.leg_type;
                    }
                }

                if (line.properties.distance === undefined)
                {
                    const dist = CoordinatesUtils.calcDistance(from, to);
                    line.properties.distance = line.properties.leg_type === "Rhumbline" ? dist.rl : dist.gc;    
                }

                route_geometry.push(line);
            }

            // Delete temporary properties
            delete waypoints[i].properties.leg_type;
            delete waypoints[i].properties.showAll;
        }

        return route_geometry;
    }

    private _insertWaypointsAtZonesCrossing(waypoints: VoyageRoutePointFeature[], zones: ZoneFeature[]): VoyageRoutePointFeature[] {
        let newWaypoints: VoyageRoutePointFeature[] = [];
        
        for (let i = 0; i < waypoints.length - 1; i++) {
            const legType = waypoints[i].properties.leg_type;
            
            newWaypoints.push(waypoints[i]);

            const crossingWaypoints: VoyageRoutePointFeature[] = [];
            const features : Feature<Geometry>[] = [];
            const lineString = new Feature({
                geometry: new LineString([waypoints[i].geometry.coordinates, waypoints[i+1].geometry.coordinates])
            });

            if (legType) {
                lineString.setProperties({"leg_type": legType});
            }

            features.push(...CreateDateLineAndGreatCircleFeatures(lineString));
            const lines = features.map(f => turfLine((<LineString>f.getGeometry()).getCoordinates()));
            
            zones!.forEach(zone => {
                lines.forEach(line => {
                    const polygons = [];
                    if (zone!.geofence!.type === "MultiPolygon") {
                        (<number[][][][]>zone!.geofence!.coordinates).forEach(p => polygons.push(<number[][][]>p));
                    }
                    if (zone!.geofence!.type === "Polygon") {
                        polygons.push(<number[][][]>zone!.geofence!.coordinates);
                    }

                    polygons.forEach(p => {
                        const intersects = lineIntersect(line, polygon(<number[][][]>p));
                        if (intersects && intersects.features.length > 0) {
                            Array.from(intersects.features).map(f => {
                                const newWaypoint = <VoyageRoutePointFeature> { 
                                    type: "Feature",
                                    geometry: {
                                        coordinates: f.geometry.coordinates,
                                        type: "Point",
                                    },
                                    properties: { type: "Hard" }
                                }

                                newWaypoint.properties.leg_type = legType;
                                if (waypoints[i].properties.speed_maximum) { newWaypoint.properties.speed_maximum = waypoints[i].properties.speed_maximum }
                                if (waypoints[i].properties.speed_minimum) { newWaypoint.properties.speed_minimum = waypoints[i].properties.speed_minimum }
                                if (waypoints[i].properties.min_depth) { newWaypoint.properties.min_depth = waypoints[i].properties.min_depth }
                                
                                return newWaypoint;
                            }).forEach(pt => {
                                const duplicate = [...crossingWaypoints, waypoints[i], waypoints[i+1]].find(existingPt => this._waypointsOverlap(existingPt, pt));
                                if (!duplicate) { 
                                    crossingWaypoints.push(pt); 
                                }
                            });
                        }
                    });
                });
            });

            // Turf intersect fuction has issues with complicated polygons (with empty spaces inside)
            // and returns the intersection points in seemingly random order; hence we have to calculate the correct order
            crossingWaypoints.sort((a, b) => {
                const aDist = legType === "Rhumbline" ? CoordinatesUtils.calcDistance(waypoints[i].geometry.coordinates, a.geometry.coordinates).rl 
                                                : CoordinatesUtils.calcDistance(waypoints[i].geometry.coordinates, a.geometry.coordinates).gc;
                const bDist = legType === "Rhumbline" ? CoordinatesUtils.calcDistance(waypoints[i].geometry.coordinates, b.geometry.coordinates).rl 
                : CoordinatesUtils.calcDistance(waypoints[i].geometry.coordinates, b.geometry.coordinates).gc;

                if (aDist < bDist) {
                    return -1;
                }
                if (aDist > bDist) {
                    return 1;
                }
                return 0;
            });

            newWaypoints.push(...crossingWaypoints);
        }

        newWaypoints.push(waypoints[waypoints.length - 1]);
        return newWaypoints;
    }
}
